<template>
  <div class="bed-board py-3 px-4">
    <div
      class="d-flex flex-row justify-content-between align-items-center mb-3"
    >
      <div class="d-flex flex-row justify-content-start align-items-center">
        <div
          class="main-image-container bg-purple rounded d-flex justify-content-center align-items-center"
        >
          <img
            height="25"
            width="25"
            :src="require('../../assets/icons/bed.svg')"
          />
        </div>
        <h3 class="mb-0 ml-3">Occupancy</h3>
      </div>
    </div>
    <hr />
    <div class="d-flex">
      <div
        class="room-container d-flex flex-column align-items-stretch w-100 mr-3"
      >
        <div
          v-for="room in rooms"
          :key="room.id"
          class="room rounded bg-white w-100 p-4"
        >
          <div class="room-name text-primary font-weight-bold mb-2">
            {{ room.name }}
          </div>
          <div class="bed-container">
            <div v-for="bed in room.beds" :key="bed.id" class="bed">
              <drop
                v-if="unassignedBedIds.includes(bed.id)"
                class="drop d-flex justify-content-center align-items-center w-100 h-100"
                :class="{ over: over[bed.id] }"
                @drop="assignEocToBed(bed, ...arguments)"
                @dragover="over = { ...over, [bed.id]: true }"
                @dragleave="over = { ...over, [bed.id]: false }"
              >
                {{ bed.name }}
              </drop>
              <drag
                v-else
                class="drag h-100"
                :transfer-data="eocMap[bedEocMap[bed.id]]"
                :effect-allowed="['link']"
                drop-effect="link"
                :hideImageHtml="false"
              >
                <template slot="image" v-if="eocMap[bedEocMap[bed.id]]">
                  <bed-eoc-template
                    :bed="bed"
                    :eoc="eocMap[bedEocMap[bed.id]]"
                    @unassign="unassingEocFromBed(bed)"
                  />
                </template>
                <template v-else>
                  <div
                    class="d-flex justify-content-center align-items-center w-100 h-100"
                  >
                    Occupied
                  </div>
                </template>
              </drag>
            </div>
          </div>
        </div>
        <div v-if="rooms.length == 0" class="room rounded bg-white w-100 p-4">
          <div class="room-name font-weight-bold text-center">
            No rooms to show
          </div>
        </div>
      </div>
      <div class="eoc-container rounded bg-white">
        <div class="ml-auto mr-3" style="width: 300px">
          <b-input v-model="search" placeholder="Search..." />
        </div>
        <div
          v-for="eoc in unassignedEocs"
          :key="eoc.id"
          class="eoc rounded w-100"
        >
          <drag
            class="drag"
            :transfer-data="eoc"
            :effect-allowed="['link']"
            drop-effect="link"
            :hideImageHtml="false"
          >
            <template slot="image">
              <eoc-draggable-template :eoc="eoc" />
            </template>
          </drag>
        </div>
        <div
          v-if="unassignedEocs.length == 0"
          class="font-weight-bold text-center"
        >
          No patients to show
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import roomsServer from "../../server/room";
import bedAssignmentServer from "../../server/bedAssignment";
import eocServer from "../../server/eoc";
import EocDraggableTemplate from "../../views/EocDraggableTemplate.vue";
import BedEocTemplate from "../../views/BedEocTemplate.vue";
import { mapState } from "vuex";

export default {
  components: { EocDraggableTemplate, BedEocTemplate },
  name: "OccupancyIndex",
  props: ["showing"],
  data: function() {
    return {
      rooms: [],
      eocs: [],
      assignments: [],
      over: {},
      search: ""
    };
  },
  methods: {
    async fetchRooms() {
      this.rooms = await roomsServer.query({
        locationId: this.selectedLocation
      });

      this.assignments = await bedAssignmentServer.query({});
    },
    async fetchEocs() {
      const result = await eocServer.queryList({
        eocStatus: ["Active", "Pending"],
        page: 0,
        pageSize: 50
      });
      this.eocs = result.episodesOfCare;
    },
    async assignEocToBed(bed, eoc) {
      this.over[bed.id] = false;
      await bedAssignmentServer.create({
        careBedId: bed.id,
        episodeOfCareId: eoc.id
      });
      await this.fetchRooms();
    },
    async unassingEocFromBed(bed) {
      const assignment = this.assignments.find(a => a.careBed.id == bed.id);
      await bedAssignmentServer.delete(assignment.id);
      await this.fetchRooms();
    }
  },
  computed: {
    ...mapState("layout", {
      selectedLocation: state => state.selectedLocation
    }),
    eocMap() {
      return this.eocs.reduce((acc, cur) => {
        return { ...acc, [cur.id]: cur };
      }, {});
    },
    bedEocMap() {
      return this.assignments.reduce((acc, cur) => {
        return { ...acc, [cur.careBed.id]: cur.episodeOfCare.id };
      }, {});
    },
    unassignedBedIds() {
      return this.rooms
        .map(r => r.beds)
        .reduce((acc, cur) => acc.concat(cur))
        .filter(bed => !this.assignments.some(ab => ab.careBed.id == bed.id))
        .map(b => b.id);
    },
    unassignedEocs() {
      return this.eocs.filter(
        eoc =>
          (this.search === "" ||
            this.formatEoc(
              eoc.patient.firstName,
              eoc.patient.middleName,
              eoc.patient.preferredName,
              eoc.patient.lastName,
              eoc.patient.mrn,
              eoc.eocn
            )
              .toLowerCase()
              .includes(this.search.toLowerCase())) &&
          !this.assignments.some(b => b.episodeOfCare.id == eoc.id)
      );
    }
  },
  watch: {
    showing: {
      handler: function(newValue) {
        if (newValue) {
          this.fetchRooms();
          this.fetchEocs();
        }
      }
    }
  }
};
</script>

<style lang="scss">
.bed-board {
  .location:hover {
    cursor: pointer;
  }

  .drag:hover {
    cursor: move;
  }

  .room-container,
  .eoc-container {
    max-height: calc(100vh - 195px);
    overflow-y: scroll;
    min-width: 375px;
  }

  .room:not(:last-child) {
    margin-bottom: 16px;
  }

  .bed-container {
    display: flex;
    gap: 20px;
    flex-wrap: wrap;
  }

  .bed {
    width: 275px;
    height: 125px;
    background-color: #f5f8fc;
    border-radius: 20px;
  }

  .bed .drop {
    /* border: 2px dashed #d0d2d4; */
    /* https://kovart.github.io/dashed-border-generator/ */
    position: relative;
    border-radius: 20px;
    background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='20' ry='20' stroke='%23D0D2D4FF' stroke-width='4' stroke-dasharray='10%2c10' stroke-dashoffset='0' stroke-linecap='butt'/%3e%3c/svg%3e");
  }

  .bed .drop.over {
    background-color: #eee;
    background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='20' ry='20' stroke='%23D0D2D4FF' stroke-width='4' stroke-dasharray='10%2c10' stroke-dashoffset='0' stroke-linecap='butt'/%3e%3c/svg%3e");
  }

  .eoc-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 16px;
    gap: 16px;
  }

  .eoc {
    background-color: #f5f8fc;
  }
}
</style>
